import React, { useState, useRef } from 'react';
import { tasksApi } from '../../api/tasks';
import toast from 'react-hot-toast';
import { X, Paperclip, FileText, Trash2 } from 'lucide-react';

const PRIORITY_COLORS = {
  urgent: 'bg-red-100 text-red-700 border-red-200',
  high: 'bg-orange-100 text-orange-700 border-orange-200',
  normal: 'bg-blue-100 text-blue-700 border-blue-200',
  low: 'bg-gray-100 text-gray-600 border-gray-200',
};

function formatFileSize(bytes) {
  if (!bytes) return '';
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / 1048576).toFixed(1) + ' MB';
}

export default function CreateTaskModal({ recordId, record, tasks, users, stages, onClose, onCreated }) {
  const [form, setForm] = useState({
    title: '',
    description: '',
    priority: 'normal',
    category: 'general',
    assigned_to: '',
    due_date: '',
    workflow_stage_id: record?.workflow_stage_id || '',
    depends_on: [],
  });
  const [pendingFiles, setPendingFiles] = useState([]);
  const [saving, setSaving] = useState(false);
  const fileInputRef = useRef(null);

  const CATEGORIES = [
    { value: 'general', label: 'General' }, { value: 'admin', label: 'Administrative' },
    { value: 'medical', label: 'Medical' }, { value: 'legal', label: 'Legal' },
    { value: 'investigation', label: 'Investigation' }, { value: 'client_comm', label: 'Client Comm' },
    { value: 'discovery', label: 'Discovery' }, { value: 'settlement', label: 'Settlement' },
  ];

  const handleFileAdd = (e) => {
    const files = Array.from(e.target.files);
    setPendingFiles(prev => [...prev, ...files]);
    fileInputRef.current.value = '';
  };

  const handleRemoveFile = (idx) => {
    setPendingFiles(prev => prev.filter((_, i) => i !== idx));
  };

  const handleCreate = async () => {
    if (!form.title.trim()) return toast.error('Title is required');
    setSaving(true);
    try {
      const res = await tasksApi.create({
        ...form,
        record_id: recordId,
        assigned_to: form.assigned_to || undefined,
        due_date: form.due_date || undefined,
        workflow_stage_id: form.workflow_stage_id || undefined,
        depends_on: form.depends_on.length > 0 ? form.depends_on : undefined,
      });
      const newTaskId = res.data.task.id;

      // Upload pending files
      for (const file of pendingFiles) {
        try {
          await tasksApi.uploadAttachment(newTaskId, file);
        } catch (e) { console.error('Failed to upload', file.name); }
      }

      toast.success('Task created');
      onCreated();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to create task');
    } finally { setSaving(false); }
  };

  const toggleDep = (taskId) => {
    setForm(prev => ({
      ...prev,
      depends_on: prev.depends_on.includes(taskId)
        ? prev.depends_on.filter(id => id !== taskId)
        : [...prev.depends_on, taskId]
    }));
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h2 className="text-lg font-semibold text-gray-900">Create New Task</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="h-5 w-5" /></button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto">
          <div className="grid grid-cols-3 gap-0">
            {/* Left — main fields */}
            <div className="col-span-2 px-6 py-5 border-r border-gray-100 space-y-4">
              {/* Title */}
              <div>
                <label className="text-xs font-medium text-gray-600 mb-1 block">Title *</label>
                <input type="text" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })}
                  placeholder="What needs to be done?"
                  autoFocus
                  className="w-full px-3 py-2.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500" />
              </div>

              {/* Description */}
              <div>
                <label className="text-xs font-medium text-gray-600 mb-1 block">Description</label>
                <textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })}
                  placeholder="Add details, instructions, or context..."
                  rows={3}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500 resize-none" />
              </div>

              {/* Dependencies */}
              {tasks.filter(t => t.status !== 'cancelled').length > 0 && (
                <div>
                  <label className="text-xs font-medium text-gray-600 mb-1.5 block">Depends On (must complete first)</label>
                  <div className="flex flex-wrap gap-1.5 max-h-32 overflow-y-auto">
                    {tasks.filter(t => t.status !== 'cancelled').map(t => {
                      const selected = form.depends_on.includes(t.id);
                      return (
                        <button key={t.id} type="button" onClick={() => toggleDep(t.id)}
                          className={'text-[10px] px-2.5 py-1.5 rounded-lg border transition-colors ' +
                            (selected
                              ? 'bg-brand-50 border-brand-300 text-brand-700 font-medium'
                              : 'border-gray-200 text-gray-500 hover:border-gray-300')}>
                          {t.task_number ? 'T-' + String(t.task_number).padStart(4, '0') + ' ' : ''}{t.title}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Attachments */}
              <div>
                <label className="text-xs font-medium text-gray-600 mb-1.5 block">Attachments</label>
                {pendingFiles.length > 0 && (
                  <div className="space-y-1.5 mb-3">
                    {pendingFiles.map((file, idx) => (
                      <div key={idx} className="flex items-center gap-3 px-3 py-2 bg-gray-50 rounded-lg border border-gray-100">
                        <FileText className="h-4 w-4 text-gray-400 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="text-xs text-gray-900 truncate">{file.name}</p>
                          <span className="text-[10px] text-gray-400">{formatFileSize(file.size)}</span>
                        </div>
                        <button onClick={() => handleRemoveFile(idx)} className="text-gray-400 hover:text-red-600">
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                <input type="file" ref={fileInputRef} onChange={handleFileAdd} className="hidden" multiple />
                <button type="button" onClick={() => fileInputRef.current?.click()}
                  className="flex items-center gap-2 px-3 py-2 text-xs text-brand-600 border border-brand-200 rounded-lg hover:bg-brand-50">
                  <Paperclip className="h-3.5 w-3.5" /> Attach Files
                </button>
              </div>
            </div>

            {/* Right sidebar — properties */}
            <div className="px-4 py-5 space-y-4">
              {/* Priority */}
              <div>
                <label className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Priority</label>
                <div className="flex flex-wrap gap-1 mt-1.5">
                  {['low', 'normal', 'high', 'urgent'].map(p => (
                    <button key={p} onClick={() => setForm({ ...form, priority: p })}
                      className={'text-[10px] px-2 py-1 rounded-md border transition-colors ' +
                        (form.priority === p
                          ? (PRIORITY_COLORS[p] || '') + ' border-current font-semibold'
                          : 'border-gray-200 text-gray-500 hover:border-gray-300')}>
                      {p.charAt(0).toUpperCase() + p.slice(1)}
                    </button>
                  ))}
                </div>
              </div>

              {/* Stage */}
              <div>
                <label className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Stage</label>
                <select value={form.workflow_stage_id} onChange={e => setForm({ ...form, workflow_stage_id: e.target.value })}
                  className="mt-1.5 w-full text-xs px-2 py-1.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-brand-500">
                  <option value="">No Stage</option>
                  {(stages || []).map(s => (
                    <option key={s.id} value={s.id}>{s.name}{record?.workflow_stage_id === s.id ? ' (current)' : ''}</option>
                  ))}
                </select>
              </div>

              {/* Assignee */}
              <div>
                <label className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Assignee</label>
                <select value={form.assigned_to} onChange={e => setForm({ ...form, assigned_to: e.target.value })}
                  className="mt-1.5 w-full text-xs px-2 py-1.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-brand-500">
                  <option value="">Unassigned</option>
                  {(users || []).map(u => <option key={u.id} value={u.id}>{u.first_name} {u.last_name}</option>)}
                </select>
              </div>

              {/* Category */}
              <div>
                <label className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Category</label>
                <select value={form.category} onChange={e => setForm({ ...form, category: e.target.value })}
                  className="mt-1.5 w-full text-xs px-2 py-1.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-brand-500">
                  {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
              </div>

              {/* Due Date */}
              <div>
                <label className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Due Date</label>
                <input type="date" value={form.due_date} onChange={e => setForm({ ...form, due_date: e.target.value })}
                  className="mt-1.5 w-full text-xs px-2 py-1.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-brand-500" />
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50">
            Cancel
          </button>
          <button onClick={handleCreate} disabled={saving || !form.title.trim()}
            className="px-4 py-2 text-sm font-medium bg-brand-600 text-white rounded-lg hover:bg-brand-700 disabled:opacity-50">
            {saving ? 'Creating...' : 'Create Task'}
          </button>
        </div>
      </div>
    </div>
  );
}
